import { createApi, retry } from "@reduxjs/toolkit/query/react";
import { createFlatBufferBaseQuery } from "../../flatbuffers/api/flatBufferBaseQuery";
import fetchAllPages from "../../utils/fetchAllPages";

const maxRetries = 2;

const baseQuery = retry(
  createFlatBufferBaseQuery(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? ""),
  { maxRetries },
);

export const keyAPI = createApi({
  reducerPath: "keyAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: [
    "tagGetAdapter",
    "tagGetBank",
    "tagGetKeys",
    "tagPostKey",
    "tagPutKey",
    "tagDeleteKey",
  ],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET BANK */
    getBank: builder.query({
      query: (body: any) => ({
        url: `/banks/${body.bankId}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetBank"],
    }),
    //* *********************************************************** */
    //* GET ALL KEYS */
    getAllKeys: builder.query({
      queryFn: async (body: any, _queryApi, _extraOptions, _baseQuery) =>
        fetchAllPages(_baseQuery, `/keys/?&page=1&limit=500`, {
          authorization: body.token,
        }),
      providesTags: ["tagGetKeys"],
    }),
    //* *********************************************************** */
    //* GET ALL KEYS LIGHT */
    getAllKeysLight: builder.query({
      queryFn: async (body: any, _queryApi, _extraOptions, _baseQuery) =>
        fetchAllPages(_baseQuery, `/keys/light/?&page=1&limit=500`, {
          authorization: body.token,
        }),
      providesTags: ["tagGetKeys"],
    }),
    //* *********************************************************** */
    //* GET ADAPTER KEYS */
    getAdapterKeys: builder.query({
      queryFn: async (body: any, _queryApi, _extraOptions, _baseQuery) =>
        fetchAllPages(
          _baseQuery,
          `/keys/?&page=1&limit=500&filter_target=adapter_id&filter=${body.adapterId}`,
          { authorization: body.token },
        ),
      providesTags: ["tagGetKeys"],
    }),
    //* *********************************************************** */
    //* POST ADAPTER KEY */
    postAdapterKey: builder.mutation({
      query: (body: any) => ({
        url: `/keys/`,
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "CreateKey" },
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* PUT ADAPTER KEY */
    putAdapterKey: builder.mutation({
      query: (body: any) => ({
        url: `/keys/${body.id}`,
        method: "PUT",
        body: { id: body.id, ...body.body },
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "UpdateKey" },
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* DELETE ADAPTER KEY */
    deleteAdapterKey: builder.mutation({
      query: (body: any) => ({
        url: `/keys/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* GET BANK KEYS */
    getBankKeys: builder.query({
      queryFn: async (body: any, _queryApi, _extraOptions, _baseQuery) =>
        fetchAllPages(
          _baseQuery,
          `/custom-keys/?&page=1&limit=500&filter_target=bank_id&filter=${body.adapterId}`,
          { authorization: body.token },
        ),
      providesTags: ["tagGetKeys"],
    }),
    //* *********************************************************** */
    //* POST BANK KEY */
    postBankKey: builder.mutation({
      query: (body: any) => ({
        url: `/custom-keys/`,
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "CreateCustomKey" },
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* PUT BANK KEY */
    putBankKey: builder.mutation({
      query: (body: any) => ({
        url: `/custom-keys/${body.id}`,
        method: "PUT",
        body: { id: body.id, ...body.body },
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "UpdateCustomKey" },
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* DELETE BANK KEY */
    deleteBankKey: builder.mutation({
      query: (body: any) => ({
        url: `/custom-keys/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* GET KEYS BY SERVER */
    getKeysByServer: builder.query({
      queryFn: async (body: any, _queryApi, _extraOptions, _baseQuery) =>
        fetchAllPages(_baseQuery, `/keys/by-server/${body.serverId}?page=1&limit=500`, {
          authorization: body.token,
        }),
      providesTags: ["tagGetKeys"],
    }),
    //* *********************************************************** */
    //* IMPORT KEY */
    importKey: builder.mutation({
      query: (body: any) => {
        const bodyFormData = new FormData();
        bodyFormData.append("csv", body.body.csv);
        bodyFormData.append("adapterID", body.body.adapter_id);
        return {
          url: `/keys/import/`,
          method: "POST",
          body: bodyFormData,
          headers: { authorization: body.token },
          formData: true,
        };
      },
      invalidatesTags: ["tagGetKeys"],
    }),
  }),
});

export const {
  useGetAllKeysQuery,
  useGetAllKeysLightQuery,
  useLazyGetBankQuery,
  useGetAdapterKeysQuery,
  useLazyGetAdapterKeysQuery,
  usePostAdapterKeyMutation,
  usePutAdapterKeyMutation,
  useDeleteAdapterKeyMutation,
  useLazyGetBankKeysQuery,
  usePostBankKeyMutation,
  usePutBankKeyMutation,
  useDeleteBankKeyMutation,
  useLazyGetKeysByServerQuery,
  useImportKeyMutation,
} = keyAPI;
